import { fetchConfig } from '@lib/section-utils';
import { PUB_SUB_EVENTS, publish, subscribe } from '@lib/pubsub';

export const CART_LABEL_KEYS = {
  PRE_ORDER_PROPERTY: 'Pre-Order Now -',
  PRE_ORDER_PROPERTY_LEGACY: 'Pre-order',
  PALLET_FREIGHT_FLAG_PROPERTY: '_Pallet Freight',
  PALLET_FREIGHT_PROPERTY_LEGACY: 'Pallet Freight Required',
  PALLET_FREIGHT_ATTRIBUTE: 'Pallet Freight',
};

const DEFAULT_PALLET_MESSAGE =
  'This product ships via LTL freight and may require lift gate service at the time of delivery if a loading dock or forklift is not available. A Wet Sounds representative will contact you to confirm all shipping and delivery details prior to shipment. Please note that return freight shipping costs are the responsibility of the customer and are not covered by Wet Sounds.';

function getPalletFreightMessage() {
  const cartRoot =
    document.querySelector('cart-drawer[data-pallet-freight-message]') ||
    document.querySelector('[data-main-cart-items][data-pallet-freight-message]');

  const messageFromDom = cartRoot?.getAttribute('data-pallet-freight-message');
  if (messageFromDom) return messageFromDom;

  if (window.cartLabelConfig?.palletFreightMessage) {
    return window.cartLabelConfig.palletFreightMessage;
  }

  if (window.cartStrings?.oversizedShippingMessage) {
    return window.cartStrings.oversizedShippingMessage;
  }

  return DEFAULT_PALLET_MESSAGE;
}

function lineItemNeedsPalletFreight(item) {
  const properties = item?.properties || {};

  if (properties[CART_LABEL_KEYS.PALLET_FREIGHT_FLAG_PROPERTY] === 'true') {
    return true;
  }

  if (properties[CART_LABEL_KEYS.PALLET_FREIGHT_PROPERTY_LEGACY]) {
    return true;
  }

  if (properties['Pallet Freight']) {
    return true;
  }

  return false;
}

function domHasPalletFreightItems() {
  return document.querySelector('[data-cart-label-pallet-freight="true"]') !== null;
}

function cartNeedsPalletFreightAttribute(cart) {
  if (cart?.items?.some(lineItemNeedsPalletFreight)) {
    return true;
  }

  return domHasPalletFreightItems();
}

export async function syncCartLabelAttributes(options = {}) {
  try {
    const cartResponse = await fetch(`${Theme.routes.cart_url}.js`, {
      credentials: 'same-origin',
    });
    if (!cartResponse.ok) return null;

    const cart = await cartResponse.json();
    const needsPalletFreight = cartNeedsPalletFreightAttribute(cart);
    const palletFreightMessage = getPalletFreightMessage();
    const currentAttribute = cart.attributes?.[CART_LABEL_KEYS.PALLET_FREIGHT_ATTRIBUTE] || '';
    const nextAttribute = needsPalletFreight ? palletFreightMessage : '';

    if (!options.force && currentAttribute === nextAttribute) {
      return { changed: false, cart };
    }

    const attributes = { ...(cart.attributes || {}) };

    if (nextAttribute) {
      attributes[CART_LABEL_KEYS.PALLET_FREIGHT_ATTRIBUTE] = nextAttribute;
    } else {
      delete attributes[CART_LABEL_KEYS.PALLET_FREIGHT_ATTRIBUTE];
    }

    const body = { attributes };
    if (options.sections?.length) {
      body.sections = options.sections;
      body.sections_url = window.location.pathname;
    }

    const updateResponse = await fetch(`${Theme.routes.cart_update_url}`, {
      ...fetchConfig('json'),
      credentials: 'same-origin',
      body: JSON.stringify(body),
    });

    if (!updateResponse.ok) {
      console.error('[cart-labels] cart/update.js failed.', updateResponse.status);
      return null;
    }

    const updatedCart = await updateResponse.json();
    publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-labels', cartData: updatedCart });

    return { changed: true, cart: updatedCart };
  } catch (error) {
    console.error('[cart-labels] Failed to sync cart attributes.', error);
    return null;
  }
}

export function getCartLabelPropertiesFromElement(element) {
  const properties = {};
  const palletFreightMessage = getPalletFreightMessage();

  if (element?.dataset?.badgeLabel) {
    properties[CART_LABEL_KEYS.PRE_ORDER_PROPERTY] = element.dataset.badgeLabel;
  }

  if (element?.dataset?.palletFreight === 'true') {
    properties[CART_LABEL_KEYS.PALLET_FREIGHT_FLAG_PROPERTY] = 'true';
    properties[CART_LABEL_KEYS.PALLET_FREIGHT_PROPERTY_LEGACY] = palletFreightMessage;
  }

  return properties;
}

export function getCartLabelPropertiesFromForm(form) {
  const properties = {};

  form?.querySelectorAll('input[name^="properties["]').forEach((input) => {
    const match = input.name.match(/^properties\[(.+)\]$/);
    if (match && input.value !== '') {
      properties[match[1]] = input.value;
    }
  });

  return properties;
}

let checkoutInProgress = false;

function getCheckoutForm(checkoutButton) {
  if (checkoutButton?.form) {
    return checkoutButton.form;
  }

  const formId = checkoutButton?.getAttribute('form');
  if (formId) {
    return document.getElementById(formId);
  }

  return (
    document.getElementById('cart-drawer') ||
    document.querySelector('form[action*="/cart"][main-cart-items]')
  );
}

function ensureCheckoutField(form) {
  let checkoutInput = form.querySelector('input[type="hidden"][name="checkout"]');

  if (!checkoutInput) {
    checkoutInput = document.createElement('input');
    checkoutInput.type = 'hidden';
    checkoutInput.name = 'checkout';
    form.appendChild(checkoutInput);
  }

  return checkoutInput;
}

function ensurePalletFreightAttributeField(form) {
  const needsPalletFreight = domHasPalletFreightItems();
  const message = getPalletFreightMessage();
  let attributeInput = form.querySelector('[data-cart-attribute="Pallet Freight"]');

  if (!needsPalletFreight || !message) {
    attributeInput?.remove();
    return;
  }

  if (!attributeInput) {
    attributeInput = document.createElement('input');
    attributeInput.type = 'hidden';
    attributeInput.name = 'attributes[Pallet Freight]';
    attributeInput.dataset.cartAttribute = 'Pallet Freight';
    form.appendChild(attributeInput);
  }

  attributeInput.value = message;
}

async function handleCheckoutNavigation(event) {
  const checkoutButton = event.target.closest('[name="checkout"]');
  if (!checkoutButton || checkoutInProgress) return;

  event.preventDefault();
  event.stopImmediatePropagation();

  checkoutInProgress = true;
  checkoutButton.setAttribute('aria-disabled', 'true');

  try {
    await syncCartLabelAttributes({ force: true });

    const form = getCheckoutForm(checkoutButton);

    if (form) {
      ensurePalletFreightAttributeField(form);
      ensureCheckoutField(form);
      form.submit();
    } else {
      window.location.assign('/checkout');
    }
  } catch (error) {
    console.error('[cart-labels] Checkout attribute sync failed.', error);
    window.location.assign('/checkout');
  } finally {
    checkoutInProgress = false;
    checkoutButton.removeAttribute('aria-disabled');
  }
}

function initCartLabelSync() {
  syncCartLabelAttributes();

  subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
    if (event.source === 'cart-labels') return;
    syncCartLabelAttributes();
  });

  document.addEventListener('click', handleCheckoutNavigation, true);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCartLabelSync);
} else {
  initCartLabelSync();
}
